import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import {
  SUMMARY_DISPLAY_OPTIONS_DROPDOWN_ID,
  SUMMARY_DISPLAY_OPTIONS_DROPDOWN,
  WORKPAPER_ACTIONS_OPTIONS_ARRAY,
  WORKPAPER_ACTIONS_TYPE,
} from 'constants/feature/statement-summary-constants';
import { annotations } from 'constants/feature/annotation-display-filter-constants';
import Checkbox from 'components/common/checkbox-component';
import Radio from 'components/common/radio-component';
import ToolTip from 'components/common/tool-tip-component';
import { renderTooltipTextBySelection } from 'constants/feature/annotation-display-filter-constants';
import AnnotationDisplayOptions from 'models/data/annotation-display-filter-options-model';
import { setWorkpaperFilterReset } from 'store/actions/annotation-display-actions';

const STATEMENT_SUMMARY_ANNOTATIONS_BLOCK = 'statement-summary-annotations';
const STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID =
  'statement-summary-annotations-id';

const WorkpaperFilterDropdownComponent = ({
  item,
  displayOptions,
  setAnnotationDisplayOption,
  workpaperList,
  workPaperTypeToShow,
  disabled,
  isSideBySideView,
}) => {
  const containerRef = useRef();
  const dispatch = useDispatch();
  const workpaperFilterReset = useSelector(
    (state) => state.ui.sideBySideView.workpaperFilterReset,
  );
  const [displayDropDown, setDisplayDropDown] = useState(false);
  const [radioOption, setRadioOption] = useState(
    WORKPAPER_ACTIONS_TYPE.SHOW_ALL,
  );

  // Reset local radio state when workpaperFilterReset flag is set
  useEffect(() => {
    if (workpaperFilterReset) {
      setRadioOption(WORKPAPER_ACTIONS_TYPE.SHOW_ALL);
      dispatch(setWorkpaperFilterReset(false));
    }
  }, [workpaperFilterReset, dispatch]);

  useEffect(() => {
    if (!displayDropDown) return;

    const onMousedownEvent = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setDisplayDropDown(false);
      }
    };
    document.addEventListener('mousedown', onMousedownEvent, false);
    return () => {
      document.removeEventListener('mousedown', onMousedownEvent, false);
    };
  }, [displayDropDown]);

  // Only render if workPaperTypeToShow exists
  if (!workPaperTypeToShow) {
    return null;
  }

  const toggleDisplayDropDown = () => {
    setDisplayDropDown(!displayDropDown);
  };

  const onCheckBoxChange = (option) => {
    let updatedOptions = displayOptions.setWorkpapersSelected(option);
    setAnnotationDisplayOption(updatedOptions);
  };

  const onRadioOptionChange = (option) => {
    let updatedOptions = displayOptions;
    if (option === WORKPAPER_ACTIONS_TYPE.SHOW_ALL) {
      updatedOptions = displayOptions.removeWorkpapersSelected();
      updatedOptions = updatedOptions.merge({
        [annotations.workpaper]: true,
      });
    } else if (option === WORKPAPER_ACTIONS_TYPE.HIDE_ALL) {
      updatedOptions = displayOptions.removeWorkpapersSelected();
      updatedOptions = updatedOptions.merge({
        [annotations.workpaper]: false,
      });
    } else {
      updatedOptions = displayOptions.merge({
        [annotations.workpaper]: true,
      });
    }
    setAnnotationDisplayOption(updatedOptions);
    setRadioOption(option);
  };

  return (
    <div
      key={item.id}
      ref={
        isSideBySideView && item.name === annotations.workpaper
          ? containerRef
          : null
      }
      id={item.id}
      className={classNames(`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element`, {
        [`${SUMMARY_DISPLAY_OPTIONS_DROPDOWN}`]:
          isSideBySideView &&
          displayDropDown &&
          item.name === annotations.workpaper,
        [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-unselected`]:
          item.name === annotations.workpaper &&
          !!displayOptions?.getWorkpapersSelected()?.length
            ? false
            : !displayOptions[item.name],
        [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-selected`]:
          (item.name === annotations.workpaper &&
            !!displayOptions?.getWorkpapersSelected()?.length) ||
          displayOptions[item.name],
        [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-unselected--disabled`]:
          !displayOptions[item.name] && disabled,
        [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-selected--disabled`]:
          displayOptions[item.name] && disabled,
      })}
      onClick={() => {
        if (isSideBySideView && item.name === annotations.workpaper) {
          toggleDisplayDropDown();
        }
      }}
    >
      <ToolTip
        id={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID}-${item.name}`}
        text={
          disabled
            ? null
            : renderTooltipTextBySelection({
                name: item.name,
                isOptionSelected: displayOptions[item.name],
                isSideBySideView:
                  isSideBySideView && item.name === annotations.workpaper,
              })
        }
        position="top"
      >
        <div>
          <FormattedMessage id={item.label} />
        </div>
      </ToolTip>
      {displayDropDown && item.name === annotations.workpaper && (
        <div
          className={classNames(
            `${SUMMARY_DISPLAY_OPTIONS_DROPDOWN}__menu`,
            `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__menu`,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__header`}>
            <Radio
              id={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID}-verify`}
              className={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__radio`}
              options={WORKPAPER_ACTIONS_OPTIONS_ARRAY}
              width="auto"
              isValid
              selectedOption={radioOption}
              onChange={(val) => onRadioOptionChange(val)}
            />
          </div>
          <div className={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__checkbox`}>
            {workpaperList?.map((item) => {
              return (
                <Checkbox
                  id={`${SUMMARY_DISPLAY_OPTIONS_DROPDOWN_ID}__metrics-${item.workpaperRefId}`}
                  name={item.referenceNumber}
                  checked={displayOptions
                    ?.getWorkpapersSelected()
                    .includes(item.referenceNumber)}
                  value={item.referenceNumber}
                  onChange={onCheckBoxChange}
                  label={item.referenceNumber}
                  key={`${item.workpaperRefId}-${item.referenceNumber}`}
                  isNotIntl={true}
                  disabled={radioOption !== WORKPAPER_ACTIONS_TYPE.SHOW_ONLY}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

WorkpaperFilterDropdownComponent.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  displayOptions: PropTypes.instanceOf(AnnotationDisplayOptions).isRequired,
  setAnnotationDisplayOption: PropTypes.func.isRequired,
  workpaperList: PropTypes.object,
  workPaperTypeToShow: PropTypes.string,
  disabled: PropTypes.bool,
  isSideBySideView: PropTypes.bool,
};

WorkpaperFilterDropdownComponent.defaultProps = {
  workpaperList: null,
  workPaperTypeToShow: null,
  disabled: false,
  isSideBySideView: false,
};

export default WorkpaperFilterDropdownComponent;
